import React, { ReactNode, useCallback, useContext } from 'react';
import { notification } from 'antd';

type NotificationType = 'success' | 'info' | 'warning' | 'error';
type NotificationPos = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export type ShowNotificationProps = {
  key?: string;
  message: string;
  description: ReactNode;
  type: NotificationType;
  placement?: NotificationPos;
  duration?: number;
};

export const NotificationContext = React.createContext<{
  showNotification: (params: ShowNotificationProps) => void;
  closeNotification: (key: string) => void;
}>({
  showNotification: () => null,
  closeNotification: () => null,
});

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [api, contextHolder] = notification.useNotification();

  const showNotification = useCallback(
    ({ duration = 4.5, type, ...params }: ShowNotificationProps) =>
      api[type]({ duration, ...params }),
    [api],
  );
  const closeNotification = useCallback(
    (key: string) => api.destroy(key),
    [api],
  );

  return (
    <NotificationContext.Provider
      value={{ showNotification, closeNotification }}
    >
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
